import { thirdwebClient, mipdStore } from '@/services/wallet-service'
import { Grid, Flex, Icon, Input, Button, Box, SimpleGrid, Text } from '@chakra-ui/react'
import { useState, useEffect, useMemo } from 'react'
import { createWallet, EIP1193, inAppWallet, WalletId } from 'thirdweb/wallets'
import { useSignInModal } from '../SigninHostModal'
import { SocialMedia } from './SocialMedia'
import EnvelopeIcon from '@/public/assets/images/sign-in/envelope.svg'
import ArrowIcon from '@/public/assets/images/sign-in/arrow.svg'
import { Image } from '@/components/HOC/Image'
import { OperationType, StatusView } from './StatusView'
import { OAuthOption } from 'node_modules/thirdweb/dist/types/wallets/types'
import { StageTitle } from './StageTitle'
import { isMobile } from '@/helpers/device'
import { useSessionStorage } from 'usehooks-ts'
import { LocalStorageKey } from '@/constants/localstorage'
import { WalletImage } from './WalletImage'
import PromoBannerImage from '@/public/assets/images/sign-in/register-banner2.png'
import { EnteringMode, useEnteringModeValue } from '@/hooks/useEnteringModeValue'
import { useTranslation } from 'next-i18next/pages'

export const RegistrationMethods = () => {
  const { t } = useTranslation()
  const [isProcessingWallet, setProcessingWallet] = useState(false)
  const [error, setError] = useState(false)
  const { navigateTo, handleWalletFlow } = useSignInModal()
  const [walletId, setWalletId] = useState<string | null>(null)
  const [wasOpened, setOpened] = useSessionStorage(LocalStorageKey.RegistrationModalAutoOpenWallet, false)
  // const {enteringMode} = useEnteringModeValue()

  const [email, setEmail] = useState<string>('')

  useEffect(() => {
    const providers = mipdStore.getProviders()
    if (isMobile() && providers.length === 1 && !wasOpened) {
      setOpened(true)
      const provider = providers.at(0)!

      handleConnectWallet(provider.info.rdns)
    }

    const wallet = inAppWallet()
    wallet
      .autoConnect({
        client: thirdwebClient,
      })
      .catch(() => null)
  }, [])

  const handleConnectWallet = async (walletId: string) => {
    setWalletId(walletId)

    try {
      const timeoutId = setTimeout(() => {
        setProcessingWallet(true)
      }, 50)

      let wallet = undefined
      let account = undefined
      if (walletId.startsWith('social')) {
        wallet = inAppWallet()
        account = await wallet.connect({
          client: thirdwebClient,
          strategy: walletId.split('-')[1] as OAuthOption,
        })
      } else {
        const provider = mipdStore.findProvider({ rdns: walletId as WalletId })
        wallet =
          provider != undefined
            ? EIP1193.fromProvider({
                provider: provider!.provider,
                walletId: walletId as any,
              })
            : createWallet(walletId as WalletId)
        account = await wallet.connect({
          client: thirdwebClient,
          walletConnect: {
            projectId: process.env.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID,
            showQrModal: true,
            qrModalOptions: { themeVariables: { '--wcm-z-index': '9999999' }, enableExplorer: false },
          },
        })
      }

      clearTimeout(timeoutId)

      handleWalletFlow(walletId, (account as any).address)
    } catch {
      setError(true)
    }
  }

  if (isProcessingWallet) {
    return (
      <StatusView
        error={error}
        isSuccess={false}
        backCallback={() => {
          setError(false)
          setProcessingWallet(false)
        }}
        provider={walletId}
        retryCallback={() => {
          setError(false)
          handleConnectWallet(walletId || '')
        }}
        operationType={walletId?.startsWith('social') ? OperationType.SocialRequest : OperationType.WalletRequest}
      />
    )
  }

  return (
    <Flex flexDir={'column'} h={'full'} justifyContent={'start'}>
      <StageTitle title={t('auth.signin-to-ninja-blaze')} textProps={{ textAlign: 'left' }} />
      <Grid maxH={'368px'} gap={{ base: 4, md: 5 }}>
        <SocialMedia handleSocialConnect={(x) => handleConnectWallet('social-' + x)} />
        <Email
          email={email}
          onEmailChange={setEmail}
          onClick={() => emailRegex.test(email) && navigateTo('EmailFlow', { email: email })}
        />
        {/* <Passkey /> */}
        <Separator />
        <WalletsButton
          email={email}
          handleEmailClick={() => emailRegex.test(email) && navigateTo('EmailFlow', { email: email })}
          handleConnectWallet={handleConnectWallet}
        />
      </Grid>
    </Flex>
  )
}

const PromoBanner = () => {
  const { enteringMode } = useEnteringModeValue()

  if ([EnteringMode.DepositBonus100, EnteringMode.Login].includes(enteringMode)) {
    return (
      <Flex justifyContent={'center'}>
        <Image w={'auto'} maxH={'90px'} alt={''} src={PromoBannerImage} />
      </Flex>
    )
  }

  return null
}

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

const Email = ({ email, onEmailChange, onClick }: any) => {
  const { t } = useTranslation()
  const [isValid, setValid] = useState(false)

  const [value, setValue] = useState(email)

  useEffect(() => {
    const isValidResult = emailRegex.test(value)
    setValid(isValidResult)
    onEmailChange(value)
  }, [value])

  return (
    <Flex h={'56px'} bg={'ninja.500'} rounded={'xl'} gap={2} alignItems={'center'}>
      <Icon color={'ninja.300'} ml={5} h={4} w={5} as={EnvelopeIcon} />
      <Input
        onChange={(e) => setValue(e.target.value)}
        value={value}
        px={0}
        border={'none'}
        placeholder={t('auth.enter-email')}
        _placeholder={{ color: 'ninja.300' }}
        fontSize={'sm'}
        fontWeight={700}
        outline={'none'}
        _focusVisible={{
          outline: 'none',
        }}
        onKeyDown={(e) => e.key === 'Enter' && isValid && onClick()}
      />
      <Button
        onClick={() => isValid && onClick()}
        flexShrink={0}
        variant={'unstyled'}
        rounded={'xl'}
        h={12}
        p={0}
        w={12}
        m={1}
        ml={0}
        bg={'ninja.700'}
        alignItems={'center'}
        color={isValid ? 'ninja.100' : 'ninja.300'}
        cursor={isValid ? 'pointer' : 'not-allowed'}
        _hover={{
          bg: isValid ? 'ninja.600' : 'ninja.700',
        }}
        border={'none'}
      >
        <Icon h={4} w={5} mt={1} as={ArrowIcon} />
      </Button>
    </Flex>
  )
}

const Separator = () => {
  const { t } = useTranslation()

  return (
    <Grid py={1} gridTemplateColumns={'1fr auto 1fr'} gap={2.5} alignItems={'center'}>
      <Box bg={'ninja.500'} h={'px'} width={'full'} />
      <Text color={'ninja.300'} fontSize={'sm'} fontWeight={700}>
        {t('common.or')}
      </Text>
      <Box bg={'#282759'} h={'px'} width={'full'} />
    </Grid>
  )
}

const WalletsButton = ({
  email,
  handleEmailClick,
  handleConnectWallet,
}: {
  email: string
  handleEmailClick: () => void
  handleConnectWallet: (walletId: WalletId) => void
}) => {
  const { t } = useTranslation()
  const { navigateTo } = useSignInModal()

  const showWalletsList = () => navigateTo('Wallets')

  const lastUsedWallet = localStorage.getItem(LocalStorageKey.LastUsedWallet) || null

  let providers = useMemo(() => {
    let providers = mipdStore
      .getProviders()
      .slice(0, 5)
      .map((x) => ({
        rdns: x.info.rdns as string,
        icon: <Image rounded={'lg'} width={8} height={8} h={'auto'} alt={x.info.name} src={x.info.icon.trim()} />,
      }))

    if (lastUsedWallet && !['email', 'social', 'inApp'].includes(lastUsedWallet.split('-').at(0)!)) {
      providers = providers.filter((x) => x.rdns != lastUsedWallet).slice(0, 4)
      providers.unshift({ rdns: lastUsedWallet, icon: <WalletImage id={lastUsedWallet} /> })
    }
    return providers
  }, [])

  if (providers.length == 0 && email.length > 0) {
    return (
      <Button
        onClick={handleEmailClick}
        rounded={'xl'}
        height={'56px'}
        fontSize={'md'}
        w={'full'}
        px={5}
        py={3}
        variant={'secondary'}
        gap={2.5}
      >
        <Icon h={4} w={5} as={EnvelopeIcon} />
        {t('auth.signin-with-email')}
      </Button>
    )
  }

  return (
    <>
      {!!providers.length ? (
        <SimpleGrid display={{ base: 'grid', sm: 'flex', md: 'grid' }} columns={6} gap={2}>
          {providers.map((p) => (
            <Button
              minW={{ base: '44px', sm: 16, md: 0 }}
              maxW={16}
              variant={'secondary'}
              h={'auto'}
              alignItems={'center'}
              justifyContent={'center'}
              p={0}
              aspectRatio={1}
              bg={'ninja.500'}
              rounded={'xl'}
              key={p.rdns}
              onClick={() => handleConnectWallet(p.rdns as WalletId)}
            >
              <Box aspectRatio={1} height={{ base: '50%', sm: 8 }} width={{ base: '50%', sm: 8 }}>
                {p.icon}
              </Box>
            </Button>
          ))}
          <Button
            flexBasis={'full'}
            onClick={showWalletsList}
            variant={'secondary'}
            h={'auto'}
            alignItems={'center'}
            justifyContent={'center'}
            p={0}
            bg={'ninja.500'}
            rounded={'xl'}
            fontSize={'xs'}
            lineHeight={1}
            color={'ninja.200'}
            gridColumnStart={providers.length + 1}
            gridColumnEnd={7}
          >
            {t('common.see-all')}
          </Button>
        </SimpleGrid>
      ) : (
        <Button
          onClick={showWalletsList}
          rounded={'xl'}
          height={'56px'}
          fontSize={'md'}
          w={'full'}
          px={5}
          py={3}
          variant={'secondary'}
          gap={2.5}
        >
          <Flex gap={1} flexWrap={'wrap'} maxW={'200px'} justify={'center'}>
            <WalletImage id="io.metamask" width={20} height={20} />
            <WalletImage id="app.phantom" width={20} height={20} />
            <WalletImage id="app.keplr" width={20} height={20} />
            <WalletImage id="com.coinbase.wallet" width={20} height={20} />
            <WalletImage id="com.trustwallet.app" width={20} height={20} />
            <WalletImage id="walletConnect" width={20} height={20} />

            <WalletImage id="io.rabby" width={20} height={20} />

            <WalletImage id="com.binance.wallet" width={20} height={20} />
          </Flex>
        </Button>
      )}
    </>
  )
}
